import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Disc3 } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { mutateListOnPost } from '@/helpers/swr.js';

import { Form } from '@/components/ui/form';
import Error from '@/components/common/Error';
import { Button } from '@/components/ui/button';

import { activityFormSchema } from './_validators';
import FormGeneral from './components/common/FormGeneral';

import { usePostActivity, useActivitiesV2 } from '@/data/activity';

const AddActivityForm = () => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postActivity = usePostActivity();
  const activities = useActivitiesV2();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(activityFormSchema),
    defaultValues: {
      title: '',
      icon: '',
      feePercentage: 0,
      isWaiverRequired: true,
      isFirstAidRequired: true,
      isQualificationRequired: true,
      isLocationRequired: true,
      servicesContractStr: 'coaching / instructing and/or guiding',
      activityQualifications: [],
      isEnabled: true
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const payload = {
        ...values,
        feePercentage: values.feePercentage * 100,
        activityQualifications: values.activityQualifications.map(
          (qual, index) => ({
            ...qual,
            sortOrder: index + 1
          })
        )
      };

      const newActivity = await postActivity(payload);
      await activities.mutate(
        (current) => mutateListOnPost(current, newActivity),
        false
      );
      navigate(`/activity`);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormGeneral />
        {apiError && <Error error={apiError} />}
        <div className="flex justify-end">
          <Button type="submit" disabled={submitting}>
            {submitting && <Disc3 className="animate-spin" />}
            Create Activity
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddActivityForm;
